export type EventTemplateId = "botez_bokeh" | "golden_serenity";

export type TemplateLayout = "modern";

export type EventTemplateConfig = {
  layout: TemplateLayout;
  palette: {
    background: string;
    surface: string;
    surfaceAlt: string;
    text: string;
    mutedText: string;
    accent: string;
    accentSoft: string;
    line: string;
  };
  hero: {
    background: string;
    backgroundImage?: string | null;
    overlay: string;
    textColor: string;
    badgeBg: string;
    badgeText: string;
    buttonBg: string;
    buttonText: string;
    buttonBorder?: string;
  };
  typography: {
    heading: string;
    body: string;
    accent?: string;
  };
  surfaces: {
    cardBackground: string;
    cardBorder: string;
    chipBackground: string;
    chipText: string;
    shadow: string;
  };
  preview: {
    background: string;
    tagline: string;
    emoji?: string;
  };
};

/**
 * The part of a template config the host-owned public surfaces read: the RSVP
 * dialog, the gift contribution dialog and the lightbox. A legacy
 * `EventTemplateConfig` satisfies it as is; an installed template package gets
 * one from `paletteConfigFor` (template-engine/resolve.ts), so both kinds of
 * template give those surfaces every colour they need.
 */
export type HostSurfaceConfig = {
  palette: EventTemplateConfig["palette"];
  typography: EventTemplateConfig["typography"];
  surfaces: EventTemplateConfig["surfaces"];
  hero: Pick<EventTemplateConfig["hero"], "buttonBg" | "buttonText">;
};

export type EventTemplateDefinition = {
  key: EventTemplateId;
  name: string;
  description: string;
  previewImage?: string | null;
  config: EventTemplateConfig;
  /**
   * Only the management endpoints send it; `GET /templates` lists active rows
   * only and leaves it out. An explicit `false` is never offered to a customer.
   */
  active?: boolean;
};

/**
 * The design a brand-new event starts on.
 *
 * Safe to change: every existing event carries an explicit theme, so nothing is
 * resolved through this default retroactively.
 */
export const EVENT_TEMPLATE_DEFAULT: EventTemplateId = "golden_serenity";

/** The event type a customer picks in the JUB-36 creation modal. */
export type EventTypeChoice = "wedding" | "baptism" | "both";

/**
 * JUB-36: the type picked in the creation modal decides which of the two
 * surviving designs (JUB-112) fits, so the customer never has to pick a
 * template on top of a type. golden_serenity reads generically enough for
 * "wedding" and "both"; botez_bokeh's peach/turquoise palette was built
 * specifically for baptism-only events.
 */
export function getDefaultTemplateForEventType(
  includesWedding: boolean,
  includesBaptism: boolean,
): EventTemplateId {
  if (includesBaptism && !includesWedding) return "botez_bokeh";
  return EVENT_TEMPLATE_DEFAULT;
}

/**
 * The designs a customer may still choose.
 *
 * JUB-112 archived every other design: retired templates were removed from
 * `SHARED_TEMPLATES` entirely (not just hidden), and a backend migration
 * remapped existing events onto these two. `SHARED_TEMPLATES` now only ever
 * contains selectable designs, so this list equals its full key set. The
 * database mirrors this via `event_templates.template_active`; this list is
 * the offline fallback for when the templates endpoint is unreachable.
 */
export const SELECTABLE_TEMPLATE_KEYS: EventTemplateId[] = ["golden_serenity", "botez_bokeh"];

export const isSelectableTemplate = (key: string): boolean =>
  SELECTABLE_TEMPLATE_KEYS.includes(key as EventTemplateId);

const SHARED_TEMPLATES: EventTemplateDefinition[] = [
  {
    key: "botez_bokeh",
    name: "Botez Bokeh",
    description: "Playful peach-and-teal bokeh design tailored for baptisms.",
    config: {
      layout: "modern",
      palette: {
        background: "#FFF2ED",
        surface: "#ffffff",
        surfaceAlt: "#FFE5DD",
        text: "#2C3E50",
        mutedText: "#5D7A8A",
        accent: "#FF6B6B",
        accentSoft: "#FFBCB8",
        line: "rgba(10,126,140,0.2)",
      },
      hero: {
        background: "linear-gradient(180deg, #FFE5DD 0%, #FFF2ED 100%)",
        backgroundImage: null,
        overlay: "none",
        textColor: "#FF6B6B",
        badgeBg: "rgba(10,126,140,0.1)",
        badgeText: "#0A7E8C",
        buttonBg: "#0A7E8C",
        buttonText: "#ffffff",
        buttonBorder: "#065F6A",
      },
      typography: {
        heading: "'Pacifico', 'Brush Script MT', cursive",
        body: "'DM Sans', 'Inter', sans-serif",
        accent: "'Bodoni Moda', 'Times New Roman', serif",
      },
      surfaces: {
        cardBackground: "#ffffff",
        cardBorder: "rgba(255,107,107,0.25)",
        chipBackground: "rgba(10,126,140,0.12)",
        chipText: "#0A7E8C",
        shadow: "0 20px 60px rgba(255,107,107,0.18)",
      },
      preview: {
        background: "linear-gradient(135deg, #FFE5DD 0%, #FFBCB8 100%)",
        tagline: "Botez jucăuș în piersică & turcoaz",
        emoji: "🕊️",
      },
    },
  },
  {
    key: "golden_serenity",
    name: "Golden Serenity",
    description: "Warm gold, sage botanicals, and calm editorial spacing.",
    config: {
      layout: "modern",
      palette: {
        background: "#FDF8F3",
        surface: "#FAF6F0",
        surfaceAlt: "#F5EFE7",
        text: "#8B7355",
        mutedText: "#9B8B7A",
        accent: "#C9A86A",
        accentSoft: "#E8DCC8",
        line: "rgba(201,168,106,0.25)",
      },
      hero: {
        background: "linear-gradient(135deg, #FDF8F3 0%, #FAF6F0 50%, #F5EFE7 100%)",
        backgroundImage: null,
        overlay: "none",
        textColor: "#8B7355",
        badgeBg: "rgba(201,168,106,0.12)",
        badgeText: "#8B7355",
        buttonBg: "#C9A86A",
        buttonText: "#FDF8F3",
        buttonBorder: "#D4A574",
      },
      typography: {
        heading: "'Lora', 'Georgia', serif",
        body: "'Nunito Sans', 'Inter', sans-serif",
        accent: "'Lora', 'Georgia', serif",
      },
      surfaces: {
        cardBackground: "rgba(253,248,243,0.97)",
        cardBorder: "rgba(232,220,200,0.4)",
        chipBackground: "rgba(201,168,106,0.12)",
        chipText: "#8B7355",
        shadow: "0 8px 40px rgba(139,115,85,0.12)",
      },
      preview: {
        background: "linear-gradient(135deg, #FDF8F3 0%, #F5EFE7 100%)",
        tagline: "Aur cald și seninătate naturală",
        emoji: "🌿",
      },
    },
  },
];

/** Every definition the app can render. Since JUB-112, that is every selectable design. */
export const FALLBACK_TEMPLATES: EventTemplateDefinition[] = SHARED_TEMPLATES;

/** Only what a customer may pick — the offline twin of the active flag in the DB. */
export const SELECTABLE_FALLBACK_TEMPLATES: EventTemplateDefinition[] =
  SHARED_TEMPLATES.filter((template) => isSelectableTemplate(template.key));

export const FALLBACK_TEMPLATE_MAP: Record<EventTemplateId, EventTemplateDefinition> = FALLBACK_TEMPLATES.reduce(
  (acc, template) => {
    acc[template.key] = template;
    return acc;
  },
  {} as Record<EventTemplateId, EventTemplateDefinition>,
);

/**
 * Resolve a theme key to a renderable definition, never throwing and never
 * returning nothing. An unknown or retired key (an event row that escaped
 * the JUB-112 migration, a stale cache, ...) lands on `EVENT_TEMPLATE_DEFAULT`
 * rather than an arbitrary registry entry, so the public page always renders
 * a real design instead of a white screen.
 */
export function resolveTemplateDefinition(
  templates: EventTemplateDefinition[] | undefined,
  key?: EventTemplateId | string | null,
): EventTemplateDefinition {
  const registry = templates && templates.length > 0 ? templates : FALLBACK_TEMPLATES;
  const match = registry.find((template) => template.key === key);
  if (match) return match;
  return registry.find((template) => template.key === EVENT_TEMPLATE_DEFAULT) ?? registry[0];
}

export function getFallbackTemplate(key?: EventTemplateId): EventTemplateDefinition {
  if (key && FALLBACK_TEMPLATE_MAP[key]) {
    return FALLBACK_TEMPLATE_MAP[key];
  }
  return FALLBACK_TEMPLATE_MAP[EVENT_TEMPLATE_DEFAULT];
}
